import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { UserPlus, CheckCircle, XCircle, Clock, Loader2, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLES = {
  pending: "border-[#F59E0B]/30 text-[#D97706] bg-[#F59E0B]/8",
  shortlisted: "border-[#0A7AFF]/30 text-[#0A7AFF] bg-[#0A7AFF]/8",
  admitted: "border-[#22C55E]/30 text-[#16A34A] bg-[#22C55E]/8",
  rejected: "border-[#FF3636]/30 text-[#DC2626] bg-[#FF3636]/8",
};

export default function Admissions() {
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ applicant_name: "", email: "", phone: "", program: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [d, s] = await Promise.all([
        axios.get(`${API}/admissions`, { withCredentials: true }),
        axios.get(`${API}/admissions/stats`, { withCredentials: true }),
      ]);
      setData(d.data);
      setStats(s.data);
    } catch {} finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/admissions/${id}`, { status }, { withCredentials: true });
    fetchAll();
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await axios.post(`${API}/admissions`, form, { withCredentials: true });
      setShowModal(false); setForm({ applicant_name: "", email: "", phone: "", program: "", notes: "" });
      fetchAll();
    } catch {} finally { setSaving(false); }
  };

  const filtered = filter === "all" ? data : data.filter(d => d.status === filter);

  const statCards = [
    { label: "Total", value: stats.total || 0, color: "#0A7AFF" },
    { label: "Pending", value: stats.pending || 0, color: "#F59E0B" },
    { label: "Shortlisted", value: stats.shortlisted || 0, color: "#8B5CF6" },
    { label: "Admitted", value: stats.admitted || 0, color: "#22C55E" },
  ];

  return (
    <div className="space-y-6" data-testid="admissions-module">
      <div className="flex items-start justify-between border-b border-[#D8D4CB] pb-5">
        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Core Academic</p>
          <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Admissions Management</h2>
        </div>
        <button onClick={() => setShowModal(true)} data-testid="add-admission-btn" className="bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-5 py-3 hover:bg-[#1C1C22] transition-colors flex items-center gap-2">
          <UserPlus className="w-3.5 h-3.5" /> New Application
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white border border-[#E2DDD4] p-5" style={{ borderLeft: `3px solid ${s.color}` }}>
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-2">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-3xl text-[#0C0C10]">{s.value}</p>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {["all", "pending", "shortlisted", "admitted", "rejected"].map(f => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`font-['Syne'] font-bold text-[9px] uppercase tracking-widest px-4 py-2 border transition-all ${filter === f ? "bg-[#0C0C10] text-[#EAFF00] border-[#0C0C10]" : "border-[#D8D4CB] text-[#7A7468] hover:border-[#0C0C10] hover:text-[#0C0C10]"}`}>
            {f}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2DDD4]">
        <div className="grid gap-0 px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7]" style={{ gridTemplateColumns: "1.5fr 1fr 1fr 80px 100px 120px" }}>
          {["Name", "Program", "Contact", "Score", "Applied", "Status"].map(h => (
            <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>
          ))}
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-[#BDBAA8]" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center font-['DM_Sans'] text-sm text-[#BDBAA8]">No applications found</div>
        ) : filtered.map((item, i) => (
          <div key={item._id} className="grid gap-0 px-5 py-3.5 border-b border-[#F0ECE4] last:border-0 hover:bg-[#FAFAF7] items-center" style={{ gridTemplateColumns: "1.5fr 1fr 1fr 80px 100px 120px" }}>
            <div>
              <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{item.applicant_name}</p>
              <p className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{item.email}</p>
            </div>
            <span className="font-['DM_Sans'] text-xs text-[#0C0C10]">{item.program}</span>
            <span className="font-['DM_Sans'] text-xs text-[#7A7468]">{item.phone}</span>
            <span className="font-['JetBrains_Mono'] text-xs text-[#0C0C10] font-bold">{item.merit_score || "-"}</span>
            <span className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{new Date(item.applied_at).toLocaleDateString("en-IN")}</span>
            <div className="flex items-center gap-1">
              <span className={`font-['Syne'] font-bold text-[9px] uppercase tracking-wide px-2 py-1 border ${STATUS_STYLES[item.status] || ""}`}>{item.status}</span>
              {item.status === "pending" && (
                <div className="flex gap-1 ml-1">
                  <button onClick={() => updateStatus(item._id, "shortlisted")} data-testid={`shortlist-${i}`} className="p-1 text-[#0A7AFF] hover:bg-[#0A7AFF]/10 transition-colors"><CheckCircle className="w-3.5 h-3.5" /></button>
                  <button onClick={() => updateStatus(item._id, "rejected")} data-testid={`reject-${i}`} className="p-1 text-[#FF3636] hover:bg-[#FF3636]/10 transition-colors"><XCircle className="w-3.5 h-3.5" /></button>
                </div>
              )}
              {item.status === "shortlisted" && (
                <button onClick={() => updateStatus(item._id, "admitted")} data-testid={`admit-${i}`} className="ml-1 font-['Syne'] font-bold text-[9px] uppercase tracking-wider text-[#22C55E] border border-[#22C55E]/30 px-2 py-1 hover:bg-[#22C55E]/8 transition-colors">Admit</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-md w-full p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10]">New Application</h3>
                <button onClick={() => setShowModal(false)} className="text-[#BDBAA8] hover:text-[#0C0C10]"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleCreate} className="space-y-5" data-testid="admission-form">
                {[["applicant_name", "Full Name", "text"], ["email", "Email", "email"], ["phone", "Phone", "tel"], ["program", "Program", "text"]].map(([field, label, type]) => (
                  <div key={field}>
                    <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">{label}</label>
                    <input type={type} value={form[field]} onChange={e => setForm(p => ({...p, [field]: e.target.value}))} required={field !== "notes"}
                      className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] placeholder:text-[#BDBAA8] transition-colors" />
                  </div>
                ))}
                <button type="submit" disabled={saving} className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="submit-admission-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Application"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
